import clsx from "clsx";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useId,
  useState
} from "react";
import { createPortal } from "react-dom";
import { ActiveSheetProvider } from "./ActiveSheetProvider";
import {
  containerStyle,
  defaultDuration,
  noDuration,
  sheetBottomStyle,
  sheetContentInnerStyle,
  sheetContentStyle,
  sheetStyle
} from "./Sheet.css";
import { useSheetContainerContext } from "./SheetContainerContext";

export interface SheetProps {
  /**
   * Class name to apply to the sheet container.
   */
  className?: string;
  /**
   * Initial value for the collapsed state provided to the context. Defaults to `false`.
   */
  initiallyCollapsed?: boolean;
}

export function Sheet({
  children,
  initiallyCollapsed = false,
  className
}: PropsWithChildren<SheetProps>) {
  const [domReady, setDomReady] = useState<boolean>(false);
  const id = useId();

  const { enableTransitions, register, unregister, sheets, setIsCollapsed } =
    useSheetContainerContext();

  useEffect(() => {
    setDomReady(true); // if we don't do this, createPortal will fail
  }, []);

  useEffect(() => {
    if (!domReady) return;
    register(id, initiallyCollapsed);

    return () => {
      unregister(id);
    };
  }, [domReady, id, initiallyCollapsed, register, unregister]);

  const index = sheets.findIndex((sheet) => sheet.id === id);
  const sheetMeta = sheets[index];

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(sheets[index].id, !sheets[index].isCollapsed);
  }, [index, setIsCollapsed, sheets]);

  const togglePreviousCollapse = useCallback(() => {
    if (index > 0) {
      setIsCollapsed(sheets[index - 1].id, !sheets[index - 1].isCollapsed);
    }
  }, [index, setIsCollapsed, sheets]);

  const getPreviousSheet = useCallback(() => {
    return {
      ...sheets[index - 1],
      toggleCollapse: togglePreviousCollapse
    };
  }, [sheets, index, togglePreviousCollapse]);

  if (!domReady || !sheetMeta) {
    return null;
  }

  return createPortal(
    <div
      className={clsx(
        sheetStyle,
        className,
        enableTransitions ? defaultDuration : noDuration
      )}
      data-collapsed={sheetMeta?.isCollapsed}
    >
      <div className={sheetContentStyle}>
        <div
          className={sheetContentInnerStyle}
          data-collapsed={sheetMeta?.isCollapsed}
        >
          <ActiveSheetProvider
            sheet={{
              ...sheetMeta,
              toggleCollapse,
              getPreviousSheet
            }}
          >
            {children}
          </ActiveSheetProvider>
        </div>
        <div className={sheetBottomStyle} />
      </div>
    </div>,
    document.getElementById("sheet-container")!
  );
}

export function SheetContainer() {
  return (
    <div
      style={{
        flex: 1,
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <div id="sheet-container" className={containerStyle} />
    </div>
  );
}
